import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/app/api/client';
import { toast } from 'react-hot-toast';

export interface AIShortcut {
  id: string;
  name: string;
  prompt: string;
}

export const DEFAULT_SHORTCUTS: AIShortcut[] = [
  { id: '1', name: 'Refine', prompt: 'Refine this text to make it clearer and more concise.' },
  { id: '2', name: 'Fix Grammar', prompt: 'Fix any spelling and grammar errors.' },
  { id: '3', name: 'Professional', prompt: 'Rewrite this to sound more professional and authoritative.' },
  { id: '4', name: 'Simplify', prompt: 'Simplify the language to make it easier to understand.' },
  { id: '5', name: 'Expand', prompt: 'Expand on these points with more detail and context.' },
];

export function useAIShortcuts() {
  const [shortcuts, setShortcuts] = useState<AIShortcut[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch shortcuts from user settings on mount
  useEffect(() => {
    const fetchShortcuts = async () => {
      try {
        setIsLoading(true);
        const response = await apiClient.get('/api/user/settings');
        const settings = response.data || {};

        let savedShortcuts = settings.aiShortcuts;

        // If no shortcuts saved, populate with defaults
        if (!savedShortcuts || !Array.isArray(savedShortcuts) || savedShortcuts.length === 0) {
          savedShortcuts = DEFAULT_SHORTCUTS;
          // We don't auto-save defaults to backend here to avoid initial load side effects,
          // but we set them in state.
        }

        setShortcuts(savedShortcuts);
      } catch (error) {
        console.error('Failed to fetch AI shortcuts:', error);
        // Fallback to defaults on error
        setShortcuts(DEFAULT_SHORTCUTS);
      } finally {
        setIsLoading(false);
      }
    };

    fetchShortcuts();
  }, []);

  // Helper to save shortcuts to backend
  const saveToBackend = async (newShortcuts: AIShortcut[]) => {
    try {
      setIsSaving(true);
      // First get current settings to merge
      const getResponse = await apiClient.get('/api/user/settings');
      const currentSettings = getResponse.data || {};

      const updatedSettings = {
        ...currentSettings,
        aiShortcuts: newShortcuts
      };

      await apiClient.put('/api/user/settings', updatedSettings);
      return true;
    } catch (error) {
      console.error('Failed to save AI shortcuts:', error);
      toast.error('Failed to save shortcuts');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Simple short ID generator
  const generateId = () => Math.random().toString(36).slice(2, 9);

  const addShortcut = useCallback(async (shortcut: Omit<AIShortcut, 'id'>) => {
    const newShortcut: AIShortcut = {
      ...shortcut,
      id: generateId()
    };

    const newShortcuts = [newShortcut, ...shortcuts];
    setShortcuts(newShortcuts);
    await saveToBackend(newShortcuts);
  }, [shortcuts]);

  const updateShortcut = useCallback(async (updatedShortcut: AIShortcut) => {
    const newShortcuts = shortcuts.map(s =>
      s.id === updatedShortcut.id ? updatedShortcut : s
    );
    setShortcuts(newShortcuts);
    await saveToBackend(newShortcuts);
  }, [shortcuts]);

  const deleteShortcut = useCallback(async (id: string) => {
    const newShortcuts = shortcuts.filter(s => s.id !== id);
    setShortcuts(newShortcuts);
    await saveToBackend(newShortcuts);
  }, [shortcuts]);

  const reorderShortcuts = useCallback(async (newShortcuts: AIShortcut[]) => {
    setShortcuts(newShortcuts);
    await saveToBackend(newShortcuts);
  }, []);

  const resetToDefaults = useCallback(async () => {
    setShortcuts(DEFAULT_SHORTCUTS);
    await saveToBackend(DEFAULT_SHORTCUTS);
  }, []);

  return {
    shortcuts,
    isLoading,
    isSaving,
    addShortcut,
    updateShortcut,
    deleteShortcut,
    reorderShortcuts,
    resetToDefaults
  };
}
